import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import { IError, IResponseData, NotificationEntityType } from 'types';
import { useError } from '../providers';
import { useAuthClient } from './useAuthClient';

export const notificationEntityUserFavoritesQueryKey =
  'notificationEntityUserFavorites';
const notificationEntityCardsQueryKey = 'getNotificationEntityCards';

function dedupeFavoriteIds(ids: string[]): string[] {
  return Array.from(new Set(ids));
}

function normalizeFavoriteIds(response: unknown): string[] {
  if (!response) {
    return [];
  }
  let raw: string[] = [];
  if (Array.isArray(response)) {
    raw = response as string[];
  } else {
    const withData = response as IResponseData<string[]>;
    if (Array.isArray(withData.data)) {
      raw = withData.data;
    }
  }
  return dedupeFavoriteIds(raw);
}

export function useNotificationEntityUserFavoritesApi({
  organizationId,
  entityType,
  enabled,
}: {
  organizationId: string;
  entityType: NotificationEntityType;
  enabled: boolean;
}) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const queryClient = useQueryClient();

  const getFavoritesUrl = useMemo(
    () =>
      `/api/v1/${organizationId}/NotificationEntity/${entityType}/get-user-favorites`,
    [organizationId, entityType],
  );

  const updateFavoritesUrl = useMemo(
    () =>
      `/api/v1/${organizationId}/NotificationEntity/${entityType}/create-update-user-favorites`,
    [organizationId, entityType],
  );

  const {
    data: favoritesResponse,
    refetch: refetchFavorites,
    isLoading: isLoadingFavorites,
  } = useQueryWithErrorHandler<unknown, IError>({
    queryKey: [
      notificationEntityUserFavoritesQueryKey,
      organizationId,
      entityType,
    ],

    queryFn: async () =>
      authClient(getFavoritesUrl, {
        method: 'POST',
      }),

    enabled: enabled && !!organizationId,
    refetchOnWindowFocus: false,
    onError: error => openModal(error),
  });

  const favoriteIds = useMemo(
    () => normalizeFavoriteIds(favoritesResponse),
    [favoritesResponse],
  );

  const { mutateAsync: saveFavoriteIds, isPending: isSavingFavorites } =
    useMutation({
      mutationFn: async (ids: string[]) => {
        await authClient(updateFavoritesUrl, {
          method: 'POST',
          data: ids as any,
        });
      },

      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [
            notificationEntityUserFavoritesQueryKey,
            organizationId,
            entityType,
          ],
        });
        await queryClient.invalidateQueries({
          queryKey: [notificationEntityCardsQueryKey, entityType],
        });
      },

      onError: (error: IError) => openModal(error),
    });

  const isFavorite = useCallback(
    (internalId: string) => favoriteIds.includes(internalId),
    [favoriteIds],
  );

  const toggleFavorite = useCallback(
    async (internalId: string) => {
      const raw = queryClient.getQueryData([
        notificationEntityUserFavoritesQueryKey,
        organizationId,
        entityType,
      ]);
      const current = normalizeFavoriteIds(raw);
      const next = current.includes(internalId)
        ? current.filter(id => id !== internalId)
        : dedupeFavoriteIds([...current, internalId]);
      await saveFavoriteIds(next);
    },
    [entityType, organizationId, queryClient, saveFavoriteIds],
  );

  return {
    favoriteIds,
    isFavorite,
    refetchFavorites,
    isLoadingFavorites,
    toggleFavorite,
    isSavingFavorites,
  };
}
